/**
 * Read of the platform's own project list (the same list Asset Planner shows).
 * Goes through the shared SDK axios instance so the refresh-token interceptor
 * applies; workspace-api and its generated client are not involved.
 */
import { queryOptions } from '@tanstack/react-query';
import type { PaginatedProjectListResponse, ProjectResponseDto } from '@monksflow/platform-api-sdk';
import { PlatformApi } from '@/lib/api/platform/platformSdk';
import { PLATFORM_PROJECTS_QUERY_KEYS, type PlatformProjectsQueryParams } from './query-keys';

/**
 * A project as the MonksFlow platform knows it: the notion Asset Planner
 * operates on, aliased so consumers don't couple to the SDK's naming. Distinct
 * from this entity's ProjectListItem/ProjectDetail (workspace-api's richer
 * record with type, dates, audiences, tags): every published workspace-api
 * project exists in the platform, but not the other way around. The
 * `platformProject*` prefix marks everything built on this notion.
 */
export type PlatformProject = ProjectResponseDto;

const MISSING_WORKSPACE_ID = '__missing-workspace-id__';

export const getPlatformProjects = async (
  workspaceId: string,
  params?: PlatformProjectsQueryParams,
): Promise<PaginatedProjectListResponse> => {
  const response = await PlatformApi.getWorkspaceProjects(workspaceId, {
    params: { name: params?.search, limit: params?.pageSize, direction: params?.direction },
  });
  return response.data;
};

export const platformProjectsQuery = (
  workspaceId: string | null | undefined,
  params?: PlatformProjectsQueryParams,
) =>
  queryOptions({
    queryKey: PLATFORM_PROJECTS_QUERY_KEYS.list(workspaceId ?? MISSING_WORKSPACE_ID, params),
    queryFn: () => getPlatformProjects(workspaceId ?? MISSING_WORKSPACE_ID, params),
    enabled: !!workspaceId,
  });
